import { useState, useEffect, useRef } from "react";
import { Button, Input, Label } from "@/components";
import { Copy, CopyCheck, ArrowRight, Zap, Download, CheckCircle2 } from "lucide-react";
import QRCodeStyling from "qr-code-styling";
import { motion, AnimatePresence } from "framer-motion";

export default function WhatsAppQRGenerator() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");

  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const qrCode = useRef<QRCodeStyling | null>(null);
  const qrRef = useRef<HTMLDivElement | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState("");

  const qrThemes = [
    { id: "rounded", type: "rounded" }, // curved card
    { id: "square", type: "square" }, // normal box
    { id: "bubble-bottom", type: "bubble-bottom" }, // chat bubble ↓
    { id: "scan-card", type: "scan-card" },
    { id: "badge", type: "badge" }, // floating scan badge
    { id: "corners", type: "corners" },
    { id: "qr-ribbon", type: "qr-ribbon" }, // scanner brackets
  ];

  const [selectedTheme, setSelectedTheme] = useState(qrThemes[0]);
  const frameRef = useRef<HTMLDivElement | null>(null);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  useEffect(() => {
    qrCode.current = new QRCodeStyling({
      width: 200,
      height: 200,
      data: "",
      margin: 12,
      dotsOptions: {
        color: "#0a0a0a",
        type: "rounded",
      },
      backgroundOptions: {
        color: "#ffffff",
      },
      cornersSquareOptions: {
        type: "extra-rounded",
        color: "#000000",
      },
      cornersDotOptions: {
        type: "dot",
        color: "#000000",
      },
    });
  }, []);
  useEffect(() => {
    if (!generatedLink || !qrCode.current) return;

    qrCode.current.update({
      data: generatedLink,
    });

    // Update main QR ref if it exists
    if (qrRef.current) {
      qrRef.current.innerHTML = "";
      qrCode.current.append(qrRef.current);
    }

    // Generate image URL for the grid previews
    qrCode.current.getRawData("png").then((rawData) => {
      if (rawData) {
        if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
        // Convert to Blob if it's a Buffer (TypeScript fix)
        const blob = rawData instanceof Blob ? rawData : new Blob([rawData as any]);
        const url = URL.createObjectURL(blob);
        setQrImageUrl(url);
      }
    });
  }, [generatedLink]);

  const downloadQR = () => {
    if (!qrCode.current) return;

    qrCode.current.download({
      name: "whatsapp-qr",
      extension: "png",
    });
  };

  const generateLink = () => {
    let isValid = true;

    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required");
      isValid = false;
    } else {
      setPhoneError("");
    }

    if (!isValid) return;

    const formattedPhone = phoneNumber.replace(/[^\d]/g, "");

    let finalMessage = message || "Hello! I'd like to connect with you...";

    const encodedMessage = encodeURIComponent(finalMessage);

    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;

    setGeneratedLink(whatsappUrl);
  };

  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        generateLink();
      }}
      className="flex flex-col gap-6"
    >
      <div>
        <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="space-y-6">
            {/* Phone Number Input */}
            <div>
              <Label htmlFor="whatsapp-number-qr">
                WhatsApp Number (with country code, without + symbol)
              </Label>
              <Input
                id="whatsapp-number-qr"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  setPhoneNumber(value);

                  if (value.trim()) {
                    setPhoneError("");
                  }
                }}
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && e.key !== "Backspace") {
                    e.preventDefault();
                  }
                }}
                placeholder="+1234567890"
                className="w-full"
              />
              {phoneError && (
                <p className="text-sm text-red-500 mt-1">{phoneError}</p>
              )}
            </div>

            {/* Message Input */}
            <div>
              <Label htmlFor="message-qr">Pre-filled Message (Optional)</Label>
              <textarea
                id="message-qr"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hello! I'd like to connect with you..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button className="ms-auto flex items-end justify-end">
                Generate QR Code
              </Button>
            </div>
          </div>
        </div>

        {/* Generated Results Section */}
        <AnimatePresence>
          {generatedLink && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="mt-6 flex flex-col justify-between w-full gap-6"
            >
              {/* Main Results Container */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-stretch bg-white p-6 rounded-md border border-gray-200 dark:border-gray-600 dark:bg-gray-800">
                <div className="lg:col-span-3 space-y-6 order-2 lg:order-1">


                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* First 3 Themes (3 columns) */}
                    {qrThemes.slice(0, 3).map((theme) => (
                      <button
                        key={theme.id}
                        type="button"
                        onClick={() => setSelectedTheme(theme)}
                        className={`group relative flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all duration-300 ${selectedTheme.id === theme.id
                          ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20 shadow-sm"
                          : "border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-emerald-200 dark:hover:border-emerald-800"
                          }`}
                      >
                        <div className="w-full aspect-square bg-gray-50 dark:bg-gray-900 rounded-lg flex items-center justify-center overflow-hidden">
                          <div className="scale-[0.50] transition-transform duration-500">
                            <QRPreview themeType={theme.type} qrImageUrl={qrImageUrl} />
                          </div>
                        </div>
                        <span className={`text-sm font-medium capitalize truncate w-full ${selectedTheme.id === theme.id ? "text-emerald-600 dark:text-emerald-400" : "text-gray-500 dark:text-gray-400"
                          }`}>
                          {theme.id.replace(/-/g, ' ')}
                        </span>
                        {selectedTheme.id === theme.id && (
                          <div className="absolute -top-2 -right-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500 fill-white dark:fill-gray-800" />
                          </div>
                        )}
                      </button>
                    ))}

                    {/* Remaining Themes (2 columns) */}
                    {qrThemes.slice(3).map((theme) => (
                      <button
                        key={theme.id}
                        type="button"
                        onClick={() => setSelectedTheme(theme)}
                        className={`group relative flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all duration-300 ${selectedTheme.id === theme.id
                          ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20 shadow-sm"
                          : "border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-emerald-200 dark:hover:border-emerald-800"
                          }`}
                      >
                        <div className="w-full aspect-square bg-gray-50 dark:bg-gray-900 rounded-sm flex items-center justify-center overflow-hidden">
                          <div className="scale-[0.50] transition-transform duration-500">
                            <QRPreview themeType={theme.type} qrImageUrl={qrImageUrl} />
                          </div>
                        </div>
                        <span className={`text-sm font-medium capitalize truncate w-full ${selectedTheme.id === theme.id ? "text-emerald-600 dark:text-emerald-400" : "text-gray-500 dark:text-gray-400"
                          }`}>
                          {theme.id.replace(/-/g, ' ')}
                        </span>
                        {selectedTheme.id === theme.id && (
                          <div className="absolute -top-2 -right-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 fill-white dark:fill-gray-800" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>


                {/* Right: Live Preview Stage (1 col on lg) */}
                <div className="flex flex-col h-full order-1 lg:order-2">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col h-full">
                    {/* Preview Header */}
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-400" />
                        <div className="w-2 h-2 rounded-full bg-yellow-400" />
                        <div className="w-2 h-2 rounded-full bg-green-400" />
                        <span className="ml-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Live Preview</span>
                      </div>
                      <Button
                        onClick={downloadQR}
                        variant="outline"
                        className="h-9 gap-2 border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-600 hover:bg-emerald-600 dark:hover:bg-emerald-600 dark:hover:border-emerald-600 dark:hover:text-white hover:text-white"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </Button>
                    </div>

                    {/* Preview Stage */}
                    <div className="relative flex-1 flex items-center justify-center p-4 md:p-12 bg-[#f8fafc] dark:bg-[#0f172a] min-h-[300px]">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
                        style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 0)', backgroundSize: '24px 24px' }} />

                      <div ref={frameRef} className="relative z-10 transition-all duration-500 transform">
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={selectedTheme.id}
                            initial={{ opacity: 0, scale: 0.95, rotateY: -10 }}
                            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                            exit={{ opacity: 0, scale: 0.95, rotateY: 10 }}
                            transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            className="flex items-center justify-center"
                          >
                            <QRPreview themeType={selectedTheme.type} qrImageUrl={qrImageUrl} isMain />
                          </motion.div>
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <div className="flex flex-col gap-4 w-full bg-white p-6 rounded-md border border-gray-200 dark:border-gray-600 dark:bg-gray-800">
                <div className="flex flex-col gap-1">
                  <h3 className="text-gray-900 dark:text-gray-100 font-bold">Your Generated Link</h3>
                  <div className="flex justify-between w-full px-3 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-500 rounded-sm text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="text"
                      value={generatedLink}
                      readOnly
                      className="w-full bg-transparent outline-none"
                    />
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className={`shrink-0 flex gap-2 items-center justify-center px-2 lg:px-4 py-2 rounded-sm border transition-all duration-300 ${copied ? "bg-green-50 border-green-300 text-green-600 dark:bg-green-900/30 dark:border-green-600 dark:text-green-400" : "bg-[#025d4b] text-white dark:border-[#025d4b]"}`}
                      title={copied ? "Copied!" : "Copy to clipboard"}
                    >
                      {copied ? (
                        <span className="flex items-center gap-2"> <CopyCheck strokeWidth={2.5} className="w-4 h-4" /><span className="hidden lg:block">Copied</span></span>
                      ) : (
                        <span className="flex items-center gap-2"> <Copy strokeWidth={2.5} className="w-4 h-4" /><span className="hidden lg:block">Copy</span> </span>
                      )}
                    </button>
                  </div>
                </div>

                <div className="divider flex items-center">
                  <hr className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap px-2">OR</span>
                  <hr className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                </div>

                <div className="bg-green-50 dark:bg-green-600/10 rounded-md border border-[#025d4b]/20 dark:border-gray-700 p-4">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                      <div className="max-w-12 w-12 h-12 bg-[#025d4b] rounded-full flex items-center justify-center shadow-lg">
                        <Zap color="white" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <h3 className="font-bold text-gray-900 dark:text-white">Automate conversations when someone clicks your link</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400">TheQuickAssist handles instant auto-replies, lead capture, and full WhatsApp CRM.</p>
                      </div>
                    </div>

                    <Button
                      onClick={() => window.open(generatedLink, "_blank")}
                      className="flex items-center gap-2 bg-[#025d4b] hover:text-white hover:border-[#025d4b] text-white  transform hover:scale-105 transition-all duration-300 rounded-full font-semibold"
                    >
                      Start free <ArrowRight size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </form>
  );
}

function QRPreview({ themeType, qrImageUrl, isMain = false }: { themeType: string, qrImageUrl: string, isMain?: boolean }) {
  const qrSize = isMain ? "w-[220px] h-[220px]" : "w-[180px] h-[180px]";

  const qrContent = qrImageUrl ? (
    <img src={qrImageUrl} alt="QR Code" className={qrSize} />
  ) : (
    <div className={`${qrSize} bg-gray-100 flex items-center justify-center text-xs text-gray-400`}>QR Code</div>
  );

  return (
    <div className={`${isMain ? "scale-100" : "scale-100"}`}>
      {/* ===== 1. CURVED CARD (MODERN) ===== */}
      {themeType === "rounded" && (
        <div className="bg-gradient-to-br from-teal-500 to-cyan-500 p-[3px] rounded-[35px] shadow-2xl">
          <div className="bg-white rounded-[30px] p-6 flex flex-col items-center gap-4">
            <div className="bg-white p-4 rounded-xl shadow-inner">
              {qrContent}
            </div>
            <div className="bg-teal-500 text-white px-8 py-2 rounded-full font-bold tracking-wider">
              SCAN ME
            </div>
          </div>
        </div>
      )}

      {/* ===== 2. SIMPLE SQUARE (CLEAN) ===== */}
      {themeType === "square" && (
        <div className="border-[6px] border-black p-6 flex flex-col items-center gap-4 bg-white shadow-lg">
          <div className="p-3 bg-white">
            {qrContent}
          </div>
          <div className="bg-black text-white px-6 py-2 font-bold text-center">
            SCAN ME
          </div>
        </div>
      )}

      {/* ===== 3. CHAT BUBBLE (BOTTOM POINTER) ===== */}
      {themeType === "bubble-bottom" && (
        <div className="relative bg-teal-500 text-white p-6 rounded-2xl flex flex-col items-center shadow-xl mb-3">
          <div className="bg-white p-4 rounded-lg">
            {qrContent}
          </div>
          <div className="mt-3 font-bold tracking-wide">SCAN ME</div>
          <div className="absolute -bottom-3 w-6 h-6 bg-teal-500 rotate-45"></div>
        </div>
      )}

      {/* ===== 4. STICKER BADGE (FLOATING LABEL) ===== */}
      {themeType === "badge" && (
        <div className="relative bg-yellow-100 border-2 border-yellow-400 p-6 rounded-2xl shadow-xl flex flex-col items-center mt-4">
          <div className="absolute -top-4 bg-yellow-400 text-black px-4 py-1 rounded-full text-sm font-bold shadow">
            SCAN ME
          </div>
          <div className="bg-white p-4 rounded-lg shadow-inner">
            {qrContent}
          </div>
        </div>
      )}

      {/* ===== 5. MESSAGE TIP FRAME (CORNER TIP) ===== */}
      {themeType === "scan-card" && (
        <div className="flex items-stretch bg-white rounded-2xl overflow-hidden shadow-lg w-fit">
          <div className="bg-black text-white px-6 flex flex-col justify-center items-center text-center">
            <div className="text-xs tracking-widest mb-1">► ► ►</div>
            <div className="text-2xl font-bold leading-none">SCAN</div>
            <div className="text-3xl font-extrabold leading-none">ME</div>
            <div className="text-xs tracking-widest mt-1">► ► ►</div>
          </div>
          <div className="w-[2px] bg-gray-200"></div>
          <div className="bg-gray-100 px-4 py-4 flex items-center justify-center">
            <div className="bg-white p-3 rounded-lg shadow-inner">
              {qrContent}
            </div>
          </div>
        </div>
      )}

      {themeType === "qr-ribbon" && (
        <div className="flex justify-center pb-12">
          <div className="relative flex flex-col items-center">
            <div className="relative bg-white p-6 rounded-3xl border-[10px] border-red-500 shadow-lg">
              <div className="bg-white p-2">
                {qrContent}
              </div>
              <div className="absolute top-3 left-3 w-6 h-6 border-l-4 border-t-4 border-black"></div>
              <div className="absolute top-3 right-3 w-6 h-6 border-r-4 border-t-4 border-black"></div>
              <div className="absolute bottom-3 left-3 w-6 h-6 border-l-4 border-b-4 border-black"></div>
              <div className="absolute bottom-3 right-3 w-6 h-6 border-r-4 border-b-4 border-black"></div>
            </div>
            <div className="absolute -bottom-14 left-1/2 -translate-x-1/2">
              <svg width="360" height="120" viewBox="0 0 360 120" className="w-[300px] h-auto">
                <polygon points="10,60 90,40 90,80" fill="#e60023" />
                <polygon points="350,60 270,40 270,80" fill="#e60023" />
                <path d="M90 50 Q180 20 270 50 L270 80 Q180 100 90 80 Z" fill="#ff1744" />
                <text x="180" y="72" textAnchor="middle" fontSize="22" fill="white" fontWeight="600" letterSpacing="2">SCAN ME</text>
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* ====== CORNERS ====== */}
      {themeType === "corners" && (
        <div className="relative p-10">
          <div className="bg-white p-4">
            {qrContent}
          </div>
          <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-black"></div>
          <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-black"></div>
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-black"></div>
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-black"></div>
          <div className="text-center mt-3 font-bold">SCAN ME</div>
        </div>
      )}
    </div>
  );
}
